import { useEffect } from 'react';
import { Car } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Shared/Button';
import { useMyVehicleTripsStore, type MyTrip } from '@/store/useMyVehicleTripsStore';
import { describeTripSpan } from './tripText';

// why: a stable empty list — a new array per render would make the store selector loop.
const NO_TRIPS: MyTrip[] = [];

interface TripDayLineProps {
  /** Local day (YYYY-MM-DD) whose trips are read. */
  day: string;
  /** Leads to the tab "Fahrten". */
  onShowTrips: () => void;
}

/**
 * Read line of the day view (FAFD-68): the own trips and rides of the day, separate from the
 * stamps — correcting the time clock does not change them. Leads to the tab "Fahrten". Shows
 * nothing on a day without trips.
 */
export function TripDayLine({ day, onShowTrips }: TripDayLineProps) {
  const { t } = useTranslation('timeclock');
  const trips = useMyVehicleTripsStore((s) => (s.dayTripsDate === day ? s.dayTrips : NO_TRIPS));
  const fetchDayTrips = useMyVehicleTripsStore((s) => s.fetchDayTrips);

  useEffect(() => {
    void fetchDayTrips(day);
  }, [day, fetchDayTrips]);

  if (trips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-[13px]">
      <Car className="w-4 h-4 shrink-0 text-slate-500" strokeWidth={2} aria-hidden />
      <span className="flex-1 min-w-0 text-slate-700 tabular-nums">
        {trips.map((trip) => describeTripSpan(trip, t)).join(' · ')}
      </span>
      <Button variant="ghost" size="sm" onClick={onShowTrips}>
        {t('trips.showAll')}
      </Button>
    </div>
  );
}
